import {
  ArrowDownLeft,
  ArrowLeftRight,
  ArrowUpRight,
  type LucideIcon,
} from 'lucide-react'
import { CallLogLabelData } from '~/types'
import {
  convertCallDuration,
  formatPhoneNumber,
  formatRelativeDay,
} from '~/utils/formatter'

import { toNounCasing } from '@mangovoice/core'
import {
  Call,
  CallLog,
  CallLogDirection,
  CallLogExtension,
  CallLogPhoneNumber,
  CallLogStatus,
} from '@mangovoice/types'

const getValueAsStringOrUnknown = (
  value: string | number | Record<string, unknown> | undefined | null,
) => {
  return value?.toString() ?? 'Unknown'
}

export const getCallDirectionIcon = ({ direction }: CallLog): LucideIcon => {
  if (direction === CallLogDirection.Internal) {
    return ArrowLeftRight
  }

  if (direction === CallLogDirection.Inbound) {
    return ArrowDownLeft
  }

  return ArrowUpRight
}

export const getCallerIdPhoneNumberExtensionOrLocation = (
  phoneNumber: CallLogPhoneNumber | undefined | null,
  extension?: CallLogExtension,
): string => {
  if (!phoneNumber) {
    return 'Unknown'
  }

  if (
    phoneNumber.caller_id &&
    phoneNumber.caller_id != 'Unavailable' &&
    phoneNumber.caller_id != ''
  ) {
    return phoneNumber.caller_id
  }

  if (extension?.number) {
    return extension.number
  }

  if (phoneNumber.phone_number) {
    return formatPhoneNumber(phoneNumber.phone_number)
  }

  return phoneNumber.location ?? 'Unknown'
}

export const setCallLogLabelTitleSubtitle = (
  labelData: CallLogLabelData,
  call: CallLog,
) => {
  if (call.direction === CallLogDirection.Internal) {
    //title is always set to the "to" number
    labelData.title = toNounCasing(
      getValueAsStringOrUnknown(
        getCallerIdPhoneNumberExtensionOrLocation(
          call.to_phone_number,
          call.to_extension,
        ),
      ),
    )

    labelData.subTitleSubject = toNounCasing(
      getValueAsStringOrUnknown(
        getCallerIdPhoneNumberExtensionOrLocation(
          call.from_phone_number,
          call.from_extension,
        ),
      ),
    )
    labelData.subTitle = `${toNounCasing(call.direction.toString())} from ${
      labelData.subTitleSubject
    }`

    labelData.titleIsPhoneNumber = false
    labelData.titleIsExtension = call.to_extension?.number ? true : false
    labelData.subTitleSubjectIsExtension = call.from_extension?.number
      ? true
      : false

    return
  }
  if (call.direction === CallLogDirection.Inbound) {
    //title is "from" number
    labelData.title = toNounCasing(
      getValueAsStringOrUnknown(
        getCallerIdPhoneNumberExtensionOrLocation(
          call.from_phone_number,
          call.from_extension,
        ),
      ),
    )

    labelData.subTitleSubject = toNounCasing(
      getValueAsStringOrUnknown(
        getCallerIdPhoneNumberExtensionOrLocation(
          call.to_phone_number,
          call.to_extension,
        ),
      ),
    )

    labelData.subTitle = `${toNounCasing(call.direction.toString())} to ${
      labelData.subTitleSubject
    }`

    labelData.titleIsPhoneNumber = formatPhoneNumber(labelData.title).length > 0
    labelData.titleIsExtension = false
    labelData.subTitleSubjectIsExtension = call.to_extension?.number
      ? true
      : false

    return
  }

  //outbound
  labelData.title = toNounCasing(
    getValueAsStringOrUnknown(
      getCallerIdPhoneNumberExtensionOrLocation(
        call.to_phone_number,
        call.to_extension,
      ),
    ),
  )

  labelData.subTitleSubject = toNounCasing(
    getValueAsStringOrUnknown(
      getCallerIdPhoneNumberExtensionOrLocation(
        call.from_phone_number,
        call.from_extension,
      ),
    ),
  )

  labelData.subTitle = `${toNounCasing(call.direction.toString())} from ${
    labelData.subTitleSubject
  }`

  labelData.titleIsPhoneNumber = formatPhoneNumber(labelData.title).length > 0
  labelData.titleIsExtension = false
  labelData.subTitleSubjectIsExtension = labelData.subTitleSubjectIsExtension =
    call.from_extension?.number ? true : false
}

export const setCallLogLabelDetailHeadings = (
  labelData: CallLogLabelData,
  call: CallLog,
) => {
  //no temporal coupling, if dependent info is missing, set it
  if (!labelData.title || !labelData.subTitle) {
    setCallLogLabelTitleSubtitle(labelData, call)
  }

  //in all cases the detailHeading is just the title
  labelData.detailHeading = labelData.title

  if (call.direction === CallLogDirection.Internal) {
    labelData.detailSubHeading = labelData.overallToNumber
    return
  }

  //inbound calls are never from an extension
  if (call.direction === CallLogDirection.Inbound) {
    if (labelData.titleIsPhoneNumber) {
      labelData.detailSubHeading = call.from_phone_number?.location ?? ''
      return
    }

    //if the title isn't a phone number, it's a caller id, subheading should be the number in this case.
    labelData.detailSubHeading = labelData.overallFromNumber
    return
  }

  //outbound
  if (labelData.titleIsPhoneNumber) {
    labelData.detailSubHeading = call.to_phone_number?.location ?? ''
    return
  }

  labelData.detailSubHeading = labelData.overallToNumber
}

export const getLabelData = (call: CallLog): CallLogLabelData => {
  const labelData: CallLogLabelData = {
    title: '',
    titleIsPhoneNumber: false,
    titleIsExtension: false,
    subTitle: '',
    subTitleSubject: '',
    subTitleSubjectIsExtension: false,
    internal: call.direction === CallLogDirection.Internal,
    detailHeading: '',
    detailSubHeading: '',
    overallDirection: call.direction,
    overallFromNumber: getValueAsStringOrUnknown(
      call.from_extension?.number ??
        formatPhoneNumber(call.from_phone_number?.phone_number ?? ''),
    ),
    overallToNumber: getValueAsStringOrUnknown(
      call.to_extension?.number ??
        formatPhoneNumber(call.to_phone_number?.phone_number ?? ''),
    ),
    date: formatRelativeDay(call.created_at),
    duration: convertCallDuration(call.duration_in_seconds).toString(),
    directionIcon: getCallDirectionIcon(call),
    missed:
      call.status === CallLogStatus.Rejected ||
      call.status === CallLogStatus.Voicemail,
  }

  setCallLogLabelTitleSubtitle(labelData, call)
  setCallLogLabelDetailHeadings(labelData, call)

  return labelData
}

export const getCallLogFromCall = (call: Call): CallLog => {
  const fromPhoneNumber: CallLogPhoneNumber = call.from.phone_number
    ? {
        caller_id: call.from.caller_id,
        location: call.from.location?.name,
        phone_number: call.from.phone_number.number,
      }
    : {
        caller_id: call.from.caller_id,
        location: call.from.location?.name,
        phone_number: call.from.formatted,
      }

  const toPhoneNumber: CallLogPhoneNumber = call.to.phone_number ?? {
    caller_id: call.to.caller_id,
    location: call.to.location?.name,
    phone_number: call.to.formatted,
  }

  return {
    id: call.id,
    pbx_id: call.pbx.id,
    uuid: '',
    started_at: call.started_at,
    ended_at: call.ended_at,
    updated_at: call.updated_at,
    from_phone_number: fromPhoneNumber,
    to_phone_number: toPhoneNumber,
    from_extension: call.from.extension,
    to_extension: call.to.extension,
    created_at: call.created_at,
    duration_in_seconds: call.duration_in_seconds,
    direction: call.direction as unknown as CallLogDirection,
    status: CallLogStatus.Answered,
  }
}
